import React, { useCallback, useState } from 'react';

import {
	RemixOverlay as RemixOptionsOverlay,
	type RemixOptionKind,
	type RemixOptionSelectionParameters,
} from './remix-overlay';
import { RemixThinkingBar } from './remix-thinking-bar';

/**
 * Props for the Remix container composed entry point.
 */
interface RemixContainerProps {
	/** Called when the user dismisses the remix flow (e.g. closes the overlay or preview). */
	onClose: () => void;
	/** Optional callback when the user selects a remix type (e.g. chart, diagram, visual aid, database). */
	onOptionSelect?: (params: RemixOptionSelectionParameters) => void;
	/**
	 * Called when "remix flow" becomes active or inactive.
	 * Handles the selection highlight (e.g. purple highlight) and thinking state start/stop flow
	 */
	onRemixFlowActiveChange?: (active: boolean) => void;
	/**
	 * When true, the thinking bar is hidden
	 * Use this when the ephemeral view (e.g. staging modal with the result) is already visible
	 * so the thinking state is not shown at the same time as the result.
	 */
	isEphemeralViewVisible?: boolean;
}

/**
 * Composed entry point for the Remix feature.
 *
 * Renders the RemixOptionsOverlay for the user to pick a remix type.
 * Thinking bar visibility is internal (flow active and not ephemeral visible).
 */
export function RemixContainer({
	onClose,
	onOptionSelect,
	onRemixFlowActiveChange,
	isEphemeralViewVisible = false,
}: RemixContainerProps): React.ReactElement {
	const [isThinking, setIsThinking] = useState(false);
	const [selectedOptionType, setSelectedOptionType] = useState<RemixOptionKind | undefined>();

	const handleOptionSelect = useCallback(
		(params: RemixOptionSelectionParameters) => {
			setIsThinking(true);
			setSelectedOptionType(params.type);
			onRemixFlowActiveChange?.(true);
			onOptionSelect?.(params);
		},
		[onOptionSelect, onRemixFlowActiveChange],
	);

	const handleStopThinking = useCallback(() => {
		setIsThinking(false);
		onRemixFlowActiveChange?.(false);
	}, [onRemixFlowActiveChange]);

	// While thinking: show thinking bar only when ephemeral is not yet visible; otherwise show nothing
	if (isThinking) {
		if (!isEphemeralViewVisible) {
			return <RemixThinkingBar onStop={handleStopThinking} optionType={selectedOptionType} />;
		}
		return <></>;
	}

	return <RemixOptionsOverlay onClose={onClose} onOptionSelect={handleOptionSelect} />;
}
